import gsap from 'gsap';
import SplitText from 'gsap/dist/SplitText';
import {
  defaultSplitCharSlideUpOptions,
  defaultSplitLineKaraokeOptions,
  defaultSplitLineRotateInOptions,
  defaultSplitRotateX3dInOptions,
} from './split.config';
import type {
  SplitCharSlideUpOptions,
  SplitLineKaraokeOptions,
  SplitLineRotateInOptions,
  SplitRotateX3dInOptions,
  SplitType,
} from './split.types';
import { fadeFromTo } from '../fade/fade';
import { slideOut, slideUp } from '../slide/slide';
import { getMinimumStaggerDuration } from './split.utils';

gsap.registerPlugin(SplitText);

export function splitElement(
  element: HTMLElement,
  splitType: SplitType,
  props?: {
    charsClass?: string;
    wordsClass?: string;
    linesClass?: string;
  },
  addInnerChild?: boolean,
  forceChars?: boolean,
): Array<HTMLElement> {
  let elements: Array<HTMLElement> = element.querySelectorAll(
    `.split-element.split-${splitType}`,
  ) as unknown as Array<HTMLElement>;

  if (elements.length === 0) {
    let type: string = splitType;
    if (splitType === 'chars') {
      type = `${splitType} words lines`;
    }

    if (splitType === 'lines') {
      type = `${splitType} ${forceChars ? 'chars' : ''}`;
    }

    if (splitType === 'words') {
      type = `${splitType} lines`;
    }

    if (element.childElementCount > 0) {
      for (const child of element.children) {
        if (child.nodeName === 'A') {
          const childLineHeight = parseInt(window.getComputedStyle(child).lineHeight, 10);
          const isMultiline = (child as HTMLElement).offsetHeight > childLineHeight * 2 - 5;
          gsap.set(child, { display: 'inline' });
          if (isMultiline) {
            child.className = 'link-underline-decoration'; // We reset the class because the :after styling doesnt work on multiline
          }
        }

        if (child.nodeName === 'A' || child.nodeName === 'B' || child.nodeName === 'I') {
          type = `words`;
        }
      }
    }

    const clonedElement = element.cloneNode(true) as HTMLElement;
    clonedElement.className = '';
    clonedElement.classList.add('visually-hidden');
    element.parentElement?.insertBefore(clonedElement, element);
    if (clonedElement.childElementCount > 0) {
      for (const child of clonedElement.children) {
        if (child.nodeName === 'A') {
          child.setAttribute('tabindex', '-1');
        }
      }
    }

    // Sometimes when two text nodes are following each other SplitText would crash
    // https://developer.mozilla.org/en-US/docs/Web/API/Node/normalize
    element.normalize();
    const split = new SplitText(element, {
      type, // 'chars,words,lines',
      charsClass: `split-element split-chars ${props?.charsClass ?? ''}`,
      wordsClass: `split-element split-words ${props?.wordsClass ?? ''}`,
      linesClass: `split-element split-lines ${props?.linesClass ?? ''}`,
    });

    if (
      type.includes('lines') &&
      props?.linesClass === 'mask-split-line' &&
      !element.classList.contains('masked-line')
    ) {
      element.classList.add('masked-line');
    }

    // I think its ok to keep the warning "Blocked aria-hidden on an element because its descendant retained focus" for <Links/>
    element.setAttribute('aria-hidden', 'true');

    elements = split[splitType] as Array<HTMLElement>;

    if (addInnerChild) {
      const innerElements: Array<HTMLElement> = [];
      elements.forEach((splitElement) => {
        const div = document.createElement('div');
        div.innerHTML = splitElement.innerHTML;

        splitElement.innerHTML = '';
        splitElement.appendChild(div);
        innerElements.push(div);
      });

      elements = innerElements;
    }
  } else if (addInnerChild) {
    const children: Array<HTMLElement> = [];
    elements.forEach((element) => {
      const child = element.querySelector('div') as HTMLElement;
      if (child) {
        children.push(child);
      }
    });

    if (children.length > 0) {
      elements = children;
    }
  }

  return elements;
}

export function splitLineRotateIn(
  element: HTMLElement,
  options: SplitLineRotateInOptions = {},
): gsap.core.Timeline {
  const {
    splitType,
    ease,
    rotation,
    transformOrigin,
    duration,
    y,
    maxStaggerDuration,
    minStaggerDuration,
    forceChars,
    linesClass = 'mask-split-line',
  } = {
    ...defaultSplitLineRotateInOptions,
    ...options,
  };
  const timeline = gsap.timeline();

  if (splitType) {
    const elements = splitElement(
      element,
      splitType,
      {
        linesClass,
      },
      true,
      forceChars,
    );

    if (element.childElementCount > 0) {
      for (const child of element.children) {
        if (child.nodeName === 'A' || child.nodeName === 'B' || child.nodeName === 'I') {
          timeline.set(child, {
            className: child.className.replace('link-underline-decoration', ''),
          });
          timeline.fromTo(
            child.children,
            {
              opacity: 0,
              yPercent: 25,
            },
            {
              ease,
              duration: 0.6,
              stagger: getMinimumStaggerDuration(
                child.children.length,
                maxStaggerDuration,
                minStaggerDuration,
              ),
              opacity: 1,
              yPercent: 0,
            },
            0,
          );
          timeline.set(
            child,
            {
              className:
                child.nodeName === 'A' ? `${child.className} link-underline-decoration` : undefined,
            },
            '>-0.25',
          );
        }
      }
    }

    timeline.add(
      fadeFromTo(element, {
        duration: 0.05,
      }),
      0,
    );
    timeline.set(element, { className: element.className.replace('overflow-visible', '') });

    timeline.fromTo(
      elements,
      {
        y,
        rotation,
        transformOrigin,
      },
      {
        // stagger,
        ease,
        duration,
        stagger: getMinimumStaggerDuration(elements.length, maxStaggerDuration, minStaggerDuration),
        rotation: 0,
        y: 0,
        transformOrigin: '0 0',
      },
      0,
    );
    timeline.set(element, { className: `${element.className} overflow-visible` }, '>-90%');
  }

  return timeline;
}

export function splitCharsSlideIn(
  element: HTMLElement,
  options: SplitCharSlideUpOptions = {},
): gsap.core.Timeline {
  const { splitType, y, duration, maxStaggerDuration, minStaggerDuration, addInnerChild } = {
    ...defaultSplitCharSlideUpOptions,
    ...options,
  };
  const timeline = gsap.timeline();

  if (splitType) {
    const elements = splitElement(
      element,
      splitType,
      {
        linesClass: 'mask-split-line',
      },
      addInnerChild,
    );

    timeline.add(
      fadeFromTo(element, {
        duration: 0.05,
      }),
      0,
    );

    if (elements.length > 0) {
      timeline.add(
        slideUp(elements, {
          stagger: getMinimumStaggerDuration(
            elements.length,
            maxStaggerDuration,
            minStaggerDuration,
          ),
          duration,
          // stagger,
          from: y,
        }),
        0,
      );
    }
  }

  return timeline;
}

export function splitCharsSlideOut(
  element: HTMLElement,
  options: SplitCharSlideUpOptions = {},
): gsap.core.Timeline {
  const { splitType, y, duration, maxStaggerDuration, minStaggerDuration } = {
    ...defaultSplitCharSlideUpOptions,
    ...{
      duration: 0.2,
      minStaggerDuration: 0.025,
      maxStaggerDuration: 0.2,
    },
    ...options,
  };
  const timeline = gsap.timeline();

  if (splitType) {
    const elements = splitElement(
      element,
      splitType,
      {
        linesClass: 'mask-split-line',
      },
      true,
    );

    timeline.add(
      slideOut(elements, {
        stagger: getMinimumStaggerDuration(elements.length, maxStaggerDuration, minStaggerDuration),
        duration,
        // stagger,
        to: y,
      }),
      0,
    );
  }

  return timeline;
}

export function splitRotateX3dIn(
  element: HTMLElement,
  options: SplitRotateX3dInOptions = {},
): gsap.core.Timeline {
  const timeline = gsap.timeline();
  const mergedOptions = { ...defaultSplitRotateX3dInOptions, ...options };
  const {
    splitType,
    z,
    y,
    skewX,
    transformOrigin,
    rotateX,
    rotateY,
    scale,
    duration,
    ease,
    autoAlpha,
    clearProps,
    maxStaggerDuration,
    minStaggerDuration,
  } = mergedOptions;

  if (splitType) {
    const elements = splitElement(element, splitType, {}, false);

    timeline.add(
      fadeFromTo(element, {
        duration: 0.05,
      }),
      0,
    );

    timeline.add(
      fadeFromTo(elements, {
        duration,
        ease: 'none',
        autoAlpha,
        clearProps: 'opacity, visibility',
        stagger: getMinimumStaggerDuration(elements.length, maxStaggerDuration, minStaggerDuration),
      }),
      0,
    );

    const transformStyle = 'preserve-3d';

    timeline.fromTo(
      elements,
      {
        rotateX,
        rotateY,
        scale,
        transformStyle,
        transformOrigin,
        z,
        y,
        skewX,
      },
      {
        duration,
        ease,
        clearProps,
        transformStyle,
        transformOrigin,
        stagger: getMinimumStaggerDuration(elements.length, maxStaggerDuration, minStaggerDuration),
        rotateX: 0,
        scale: 1,
        rotateY: 0,
        z: 0,
        y: 0,
        skewX: 0,
      },
      '<',
    );
  }

  return timeline;
}

export function splitLineKaraoke(
  element: HTMLElement,
  options: SplitLineKaraokeOptions = {},
): gsap.core.Timeline {
  const { splitType, ease, duration } = {
    ...defaultSplitLineKaraokeOptions,
    ...options,
  };
  const timeline = gsap.timeline();

  if (splitType) {
    const elements = splitElement(element, splitType, {}, false);
    timeline.fromTo(
      elements,
      {
        maskImage: 'linear-gradient(to right, #000 0%, #0006 0%)',
      },
      {
        ease,
        duration,
        stagger: 1,
        maskImage: 'linear-gradient(to right, #000 100%, #0006 100%)',
      },
      0,
    );
  }

  return timeline;
}
